/**
 * Save flow for the Candid document-upload modal.
 *
 * Maps {@link CandidDocumentUploadFormState} onto
 * {@link CandidDocumentUploadRequest} and POSTs it via
 * {@link CandidService.postDocument}. Failures set `submitError` and an error
 * toast; success invokes `onSuccess` so the caller can refetch, close, and
 * toast.
 *
 * @module useCandidDocumentUploadSave
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { CandidDocumentUploadRequest } from "../../../../api/services/candid/candidApiTypes";
import { CandidService } from "../../../../api/services/candid/candidService";
import { CandidDocumentUploadFormState } from "../../../../data-model/candidDocumentUploadFormState";
import { ToastMessages } from "../../../toasts/toastMessages";

/** Order-level identifiers the form does not carry. */
export interface CandidDocumentUploadContext {
  /** PA order identifier. */
  orderId: string;
  /** Organisation identifier. */
  orgId: string;
  /** Patient MRN from `generalOrderDetails.demographics`. */
  mrn: string;
}

/**
 * Props for {@link useCandidDocumentUploadSave}.
 */
interface UseCandidDocumentUploadSaveProps {
  /** Latest form snapshot from {@link NewDocUploadBody}. */
  formState: CandidDocumentUploadFormState;
  /** Order / org / MRN for the request body. */
  context: CandidDocumentUploadContext;
  /** Invoked after a successful POST (refetch, close, toast). */
  onSuccess: () => void | Promise<void>;
}

/**
 * Maps the form snapshot and order context onto the document-upload request.
 *
 * @param formState - Document name and GCS path from the modal body
 * @param context - Order, organisation, and MRN identifiers
 * @returns Body for {@link CandidService.postDocument}
 */
const buildCandidDocumentUploadRequest = (
  formState: CandidDocumentUploadFormState,
  context: CandidDocumentUploadContext,
): CandidDocumentUploadRequest => ({
  order_id: context.orderId,
  org_id: context.orgId,
  mrn: context.mrn,
  document_gcs_path: formState.filePath,
  display_name: formState.docName,
});

/**
 * Posts the selected document to Candid and tracks in-flight / error state.
 *
 * @param props.formState - Current form snapshot
 * @param props.context - Order-level request fields
 * @param props.onSuccess - Called after a successful upload
 * @returns Save handler, disabled flag, button label, and error message
 */
export const useCandidDocumentUploadSave = ({
  formState,
  context,
  onSuccess,
}: UseCandidDocumentUploadSaveProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Transforms the form and POSTs it. No-ops while invalid or already in flight.
   * On success, calls `onSuccess`. On failure, records `submitError` and toasts.
   *
   * @throws Surfaces HTTP failures as `submitError` and an error toast; does not rethrow.
   */
  const handleSave = useCallback(async () => {
    if (!formState.isValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await CandidService.postDocument(
        buildCandidDocumentUploadRequest(formState, context),
      );
      await onSuccess();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : ToastMessages.CANDID_DOCUMENT_UPLOAD_ERROR;
      setSubmitError(message);
      toast.error(ToastMessages.CANDID_DOCUMENT_UPLOAD_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, context, isSubmitting, onSuccess]);

  const isSaveDisabled = !formState.isValid || isSubmitting;
  const saveButtonText = isSubmitting ? "Uploading..." : "Trigger Upload";

  return {
    handleSave,
    isSaveDisabled,
    saveButtonText,
    isSubmitting,
    submitError,
  };
};
